// ahri_v5 — domain/ecoRebates.js

export function getEcoRebatesContainerId(ahriId, context = "card") {
  const normalized = String(ahriId || "").replace(/[^A-Za-z0-9_-]+/g, "");
  const normalizedContext = String(context || "card").replace(/[^A-Za-z0-9_-]+/g, "");
  return normalized ? `impqx-eco-rebates-${normalized}-${normalizedContext || "card"}` : "";
}
export function hasEcoRebateContentText(rawText) {
  const text = String(rawText || "").replace(/\s+/g, " ").trim();
  if (!text) return false;
  if (/no\s+(rebates?|offers?|incentives?|programs?|results?)/i.test(text)) return false;
  if (/not\s+available|unavailable|no\s+matching/i.test(text)) return false;
  // Treat explicit zero-result payloads as "no rebate" even if widget copy still includes
  // generic marketing words like "rebate" and "$" values.
  if (/\b0\s+(?:energy\s+)?rebates?\b/i.test(text)) return false;
  if (/\b0\s+(?:offers?|incentives?|programs?)\b/i.test(text)) return false;
  if (/up\s*to\s*\$?\s*0(?:[.,]0+)?\b/i.test(text)) return false;
  const hasProgramWords = /rebate|incentive|tax\s*credit|utility|program/i.test(text);
  const hasAmountWords = /\$\s*\d|\/\s*ton\b|up\s*to\s+\$?\d/i.test(text);
  return hasProgramWords && hasAmountWords;
}

export function isEcoRebateNativeClickTarget(element) {
  if (!element || typeof element.closest !== "function") return false;
  return !!element.closest("a, [class*='ecr']");
}

export function findEcoRebateNativeClickTarget(container) {
  if (!container || typeof container.querySelector !== "function") return null;
  const selectors = [
    'a[href]:not([href="#"]):not([href=""])',
    "a",
    '[class*="ecr"][ng-click]',
    '[class*="ecr"]',
  ];
  for (let idx = 0; idx < selectors.length; idx += 1) {
    const match = container.querySelector(selectors[idx]);
    if (match) return match;
  }
  return null;
}

export function triggerEcoRebateNativeClick(containerOrId) {
  let container = containerOrId;
  if (typeof containerOrId === "string") {
    if (typeof document === "undefined") return false;
    container = document.getElementById(containerOrId);
  }
  if (!container) return false;
  const clickTarget = findEcoRebateNativeClickTarget(container);
  if (!clickTarget) return false;
  try {
    clickTarget.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    return true;
  } catch {
    try {
      clickTarget.click();
      return true;
    } catch {
      return false;
    }
  }
}

export function formatEcoRebateDate(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  let dateObj = null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map((part) => Number(part));
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
      dateObj = new Date(Date.UTC(y, m - 1, d));
    }
  }
  if (!dateObj) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) dateObj = parsed;
  }
  if (!dateObj) return raw;
  try {
    return dateObj.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return raw;
  }
}

export function pickRebateProgramDisplayAmount(program) {
  if (!program || typeof program !== "object") return "";
  const candidates = [
    program.displayAmount,
    program.offerAmountDisplay,
    program.totalAmountDisplay,
    program.offerAmount,
    program.totalAmount,
    program.amount,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const text = String(candidate).trim();
    if (!text) continue;
    if (typeof candidate === "number") {
      if (!Number.isFinite(candidate) || candidate <= 0) continue;
      return `$${candidate.toLocaleString()}`;
    }
    if (/^\$?0(?:[.,]0+)?$/.test(text.replace(/\s+/g, ""))) continue;
    return text.startsWith("$") || /[a-z%]/i.test(text) ? text : `$${text}`;
  }
  return "";
}

export function pickRebateProgramAction(program) {
  if (!program || typeof program !== "object") return null;
  const directAction = program.redemptionAction || program.action;
  if (directAction && typeof directAction === "object") {
    const url = String(directAction.url || directAction.href || "").trim();
    const label = String(directAction.label || directAction.text || "").trim();
    if (url) return { url, label: label || "Get rebate details" };
  }
  const fallbackUrl = String(program.url || program.programUrl || program.detailsUrl || "").trim();
  if (fallbackUrl) return { url: fallbackUrl, label: "Get rebate details" };
  return null;
}

export function summarizeRebateOfferType(program) {
  if (!program || typeof program !== "object") return "";
  const candidates = [
    program.offerTypeLabel,
    program.offerType,
    program.offerCategory,
    program.programType,
  ];
  const typeStr = candidates
    .map((entry) => (entry == null ? "" : String(entry).trim()))
    .find((entry) => entry.length > 0) || "";
  const offerTypes = Array.isArray(program.offerTypes) ? program.offerTypes : [];
  const tags = offerTypes
    .map((entry) => String(entry || "").trim())
    .filter(Boolean)
    .map((entry) => entry.replace(/_/g, " "));
  if (typeStr && tags.length) {
    return `${typeStr} • ${tags.join(", ")}`;
  }
  return typeStr || tags.join(", ");
}
